import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchVariableLines } from '../services/api';
import Chip from './Chip';
import Empty from './Empty';
import { formatMinor } from '../lib/money';

/**
 * Variable pay lines for a run or an employee (SKPEOPLE-UICC-001 CH-U2).
 *
 * READ-ONLY BY DESIGN. There is no edit action, and that is not an omission:
 * corrections happen at the SOURCE vertical via a reversal, and the console
 * renders lines rather than mutating them. An editable amount here would let
 * payroll silently disagree with the settlement it came from.
 */
export default function VariableLinesTable(props: { runId?: string; employeeId?: string; periodCode?: string }) {
  const scope = props.runId ?? props.employeeId ?? props.periodCode ?? 'all';

  const { data: lines = [], isLoading, isError } = useQuery({
    queryKey: qk.variableLines(scope),
    queryFn: () => fetchVariableLines({
      runId: props.runId, employeeId: props.employeeId, periodCode: props.periodCode,
    }),
  });

  if (isError) {
    return <p style={{ color: 'var(--danger, #dc2626)', fontSize: 'var(--fs-sm)' }}>Could not load variable lines.</p>;
  }
  if (isLoading) {
    return <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>;
  }
  if (lines.length === 0) {
    return <Empty title="No variable lines" description="Trip pay, tips, fines and reimbursements appear here." icon="receipt" />;
  }

  // Reversal pairs are visually linked so a reader can see which line cancels
  // which, rather than two unrelated-looking amounts.
  const pairedWith = new Map<string, string>();
  lines.forEach((l) => { if (l.reversesLineId) pairedWith.set(l.reversesLineId, l.id); });

  return (
    <table className="t-table">
      <thead>
        <tr>
          <th>Component</th>
          <th>Kind</th>
          <th>Source</th>
          <th>Period</th>
          <th style={{ textAlign: 'right' }}>Amount</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id} style={l.isReversal ? { opacity: 0.85 } : undefined}>
            <td>
              {l.isReversal ? `Reversal: ${l.componentCode}` : l.componentCode}
              {pairedWith.has(l.id) && (
                <Chip variant="neutral" style={{ marginLeft: 6 }}>reversed</Chip>
              )}
              {l.originalPeriodCode && (
                // Arrears badge with the period it came from (CH-U2).
                <Chip variant="warn" style={{ marginLeft: 6 }}>arrears {l.originalPeriodCode}</Chip>
              )}
            </td>
            <td><Chip variant={l.kind === 'earning' ? 'success' : 'danger'}>{l.kind}</Chip></td>
            <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>
              {l.sourceModule} · {l.sourceRef}
            </td>
            <td className="mono" style={{ fontSize: 'var(--fs-xs)' }}>{l.periodCode}</td>
            <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
              {l.kind === 'deduction' ? '−' : ''}{formatMinor(l.amountMinor, l.currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
